import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MapPin, Clock, ImageIcon } from 'lucide-react';
import { TeamAApi, ApiError } from '@ai-tiaozs/shared';
import { PrimaryButton } from '@/components/ImageUploader';
import { VoiceInput } from '@/components/VoiceInput';
import { ImageUploader } from '@/components/ImageUploader';

/**
 * A 队 · 第 2 步 智能寻找(肖淑慧)
 * 2.0:
 *  - 描述 / 参考图 两种模式(描述输入框支持文字和语音)
 *  - 最后见到地点 / 时间(可选)
 *  - 候选结果含 AI 追问(多候选相似时主动追问)
 *  - "申请认领此物品"按钮
 */
type Mode = 'text' | 'image';

export function TeamASearch() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('text');
  const [text, setText] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [lastSeenLocation, setLastSeenLocation] = useState('');
  const [lastSeenTime, setLastSeenTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (mode === 'image' && !imageDataUrl) {
      setError('请上传一张参考照片');
      return;
    }
    if (mode === 'text' && !text.trim()) {
      setError('请描述一下你丢了什么,可以打字或点击麦克风口述');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await TeamAApi.search({
        mode,
        queryText: text.trim() || undefined,
        queryImageDataUrl: imageDataUrl ?? undefined,
        lastSeenLocation: lastSeenLocation.trim(),
        lastSeenTime: lastSeenTime.trim(),
      });
      sessionStorage.setItem('ab_search_result', JSON.stringify(r));
      sessionStorage.setItem('ab_search_input', JSON.stringify({ text, lastSeenLocation, lastSeenTime, mode }));
      navigate('/search/results');
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError('网络错误,请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">② 智能寻找</h1>
        <p className="text-xs text-slate-500">用文字、语音或参考图描述你丢的物品</p>
      </header>

      <section className="card space-y-3">
        <div className="rounded-md bg-teamA-primary/5 p-3 text-xs text-slate-600">
          <p className="flex items-center gap-1 font-semibold text-teamA-primary">
            <Sparkles className="h-3.5 w-3.5" /> 描述越具体 AI 越准
          </p>
          <p className="mt-1">
            推荐说清楚: <span className="text-slate-700">颜色 + 类别 + 文字/图案 + 丢失地点</span>
          </p>
          <p className="mt-0.5 text-slate-500">
            例:深蓝色水杯,杯身有卡通恐龙挂件,杯底贴纸写着名字
          </p>
        </div>

        {/* 模式选择 */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: 'text', label: '描述失物', icon: Sparkles },
            { v: 'image', label: '参考照片', icon: ImageIcon },
          ].map((m) => {
            const Icon = m.icon;
            const active = mode === (m.v as Mode);
            return (
              <button
                key={m.v}
                onClick={() => setMode(m.v as Mode)}
                className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[11px] ${
                  active ? 'border-teamA-primary bg-teamA-primary/5' : 'border-slate-200'
                }`}
              >
                <Icon className="h-4 w-4 text-teamA-primary" />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* 输入区 */}
        {mode !== 'image' && (
          <div>
            <label className="text-xs text-slate-500">描述失物(可打字或点麦克风口述)</label>
            <div className="mt-1">
              <VoiceInput
                value={text}
                onChange={setText}
                placeholder="深蓝色水杯,杯身有卡通恐龙挂件,杯底贴纸写着名字"
                maxLength={300}
                multiline
                rows={4}
                ariaLabel="失物描述"
              />
            </div>
          </div>
        )}

        {mode === 'image' && (
          <div>
            <label className="text-xs text-slate-500">参考照片</label>
            <div className="mt-1">
              <ImageUploader label="上传参考图" onChange={setImageDataUrl} />
            </div>
          </div>
        )}

        {/* 最后见到地点 / 时间(可选) */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-slate-500">
              <MapPin className="mr-1 inline h-3 w-3" />
              最后见到地点(可选)
            </label>
            <div className="mt-1">
              <VoiceInput
                value={lastSeenLocation}
                onChange={setLastSeenLocation}
                placeholder="操场"
                maxLength={40}
                ariaLabel="最后见到地点"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">
              <Clock className="mr-1 inline h-3 w-3" />
              最后见到时间(可选)
            </label>
            <div className="mt-1">
              <VoiceInput
                value={lastSeenTime}
                onChange={setLastSeenTime}
                placeholder="今天上午第三节课"
                maxLength={40}
                ariaLabel="最后见到时间"
              />
            </div>
          </div>
        </div>

        {error && <p className="rounded-md bg-teamA-danger/10 px-3 py-2 text-xs text-teamA-danger">{error}</p>}

        <PrimaryButton loading={loading} loadingText="AI 匹配中…" onClick={submit}>
          开始寻找
        </PrimaryButton>
      </section>
    </div>
  );
}
